import { useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, ArrowLeft, ArrowRight, GripVertical } from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 9);

// Unisce celle consecutive uguali nella prima colonna (effetto rowspan)
function firstColSpans(rows) {
  const spans = [];
  for (let i = 0; i < rows.length; i++) {
    if (i > 0 && (rows[i - 1]?.[0] || "") === (rows[i]?.[0] || "") && (rows[i]?.[0] || "") !== "") {
      spans.push({ first: false, span: 1 });
    } else {
      let span = 1;
      while (i + span < rows.length && (rows[i + span]?.[0] || "") === (rows[i]?.[0] || "") && (rows[i]?.[0] || "") !== "") span++;
      spans.push({ first: true, span });
    }
  }
  return spans;
}

export function FlexibleTableView({ columns = [], rows = [], caption }) {
  if (!columns.length) return null;
  const spans = firstColSpans(rows);
  return (
    <div data-testid="flextable-view" className="rounded-xl border border-white/10 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-[#020617] text-left">
              {columns.map((c, i) => (
                <th key={i} className="px-4 py-3 font-semibold text-gold border-b border-white/10">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className="border-b border-white/5 hover:bg-white/[0.02]">
                {columns.map((_, ci) => {
                  if (ci === 0) {
                    if (!spans[ri]?.first) return null;
                    return (
                      <td key={ci} rowSpan={spans[ri].span} className="px-4 py-3 align-top font-semibold text-foreground border-r border-white/10 bg-[#0F172A]">
                        {row[ci] || "—"}
                      </td>
                    );
                  }
                  return (
                    <td key={ci} className="px-4 py-3 text-muted-foreground border-r border-white/10 last:border-r-0">
                      {row[ci] ? <span className="text-foreground/90">{row[ci]}</span> : <span className="italic text-muted-foreground/50">—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {caption ? <div className="px-4 py-2 text-xs text-muted-foreground bg-[#0B1220] border-t border-white/10">{caption}</div> : null}
    </div>
  );
}

function RowItem({ row, ri, cols, testid, onCell, onRemove }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={row} dragListener={false} dragControls={controls} className="flex items-start gap-2 bg-[#0B1220]">
      <button
        data-testid={`${testid}-row-${ri}-drag`}
        onPointerDown={(e) => controls.start(e)}
        className="p-2 mt-1 text-muted-foreground hover:text-gold cursor-grab active:cursor-grabbing touch-none"
        title="Trascina per spostare"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="flex-1 grid gap-2" style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(0,1fr))` }}>
        {cols.map((_, ci) => (
          <Input key={ci} data-testid={`${testid}-cell-${ri}-${ci}`} value={row.cells[ci] ?? ""} onChange={(e) => onCell(row.id, ci, e.target.value)} className="h-9 text-sm" />
        ))}
      </div>
      <button data-testid={`${testid}-row-${ri}-remove`} onClick={() => onRemove(row.id)} className="p-2 text-muted-foreground hover:text-destructive pt-2"><Trash2 className="w-4 h-4" /></button>
    </Reorder.Item>
  );
}

export function FlexibleTableEditor({ columns = [], rows = [], onChange, testid = "flextable" }) {
  const [cols, setCols] = useState(() => (columns.length ? columns : ["Colonna 1"]));
  const [krows, setKrows] = useState(() => (rows.length ? rows : [["", ""]]).map((r) => ({ id: uid(), cells: [...r] })));

  const push = (nc, nr) => { setCols(nc); setKrows(nr); onChange({ columns: nc, rows: nr.map((r) => r.cells) }); };

  const renameCol = (i, v) => push(cols.map((c, idx) => (idx === i ? v : c)), krows);
  const addCol = () => push([...cols, `Colonna ${cols.length + 1}`], krows.map((r) => ({ ...r, cells: [...r.cells, ""] })));
  const removeCol = (i) => { if (cols.length <= 1) return; push(cols.filter((_, idx) => idx !== i), krows.map((r) => ({ ...r, cells: r.cells.filter((_, idx) => idx !== i) }))); };
  const moveCol = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= cols.length) return;
    const nc = [...cols]; [nc[i], nc[j]] = [nc[j], nc[i]];
    const nr = krows.map((r) => { const cc = [...r.cells]; [cc[i], cc[j]] = [cc[j], cc[i]]; return { ...r, cells: cc }; });
    push(nc, nr);
  };
  const setCell = (rowId, ci, v) => push(cols, krows.map((r) => (r.id === rowId ? { ...r, cells: r.cells.map((c, x) => (x === ci ? v : c)) } : r)));
  const addRow = () => push(cols, [...krows, { id: uid(), cells: cols.map(() => "") }]);
  const removeRow = (rowId) => push(cols, krows.filter((r) => r.id !== rowId));
  const reorder = (nr) => push(cols, nr);

  return (
    <div data-testid={`${testid}-editor`} className="rounded-xl border border-white/10 p-4 space-y-4 bg-[#0B1220]">
      {/* Colonne */}
      <div className="flex flex-wrap gap-2 items-end">
        {cols.map((c, i) => (
          <div key={i} className="flex flex-col gap-1">
            <div className="flex gap-0.5">
              <button data-testid={`${testid}-col-${i}-left`} onClick={() => moveCol(i, -1)} className="p-1 text-muted-foreground hover:text-gold"><ArrowLeft className="w-3 h-3" /></button>
              <button data-testid={`${testid}-col-${i}-right`} onClick={() => moveCol(i, 1)} className="p-1 text-muted-foreground hover:text-gold"><ArrowRight className="w-3 h-3" /></button>
              <button data-testid={`${testid}-col-${i}-remove`} onClick={() => removeCol(i)} className="p-1 text-muted-foreground hover:text-destructive"><Trash2 className="w-3 h-3" /></button>
            </div>
            <Input data-testid={`${testid}-col-${i}-name`} value={c} onChange={(e) => renameCol(i, e.target.value)} placeholder={`Colonna ${i + 1}`} className="h-8 w-36 text-xs font-semibold text-gold" />
          </div>
        ))}
        <Button data-testid={`${testid}-add-col`} variant="outline" size="sm" onClick={addCol} className="border-white/15 hover:border-gold/50 h-8"><Plus className="w-3.5 h-3.5 mr-1" /> Colonna</Button>
      </div>

      {/* Righe (drag & drop) */}
      <Reorder.Group axis="y" values={krows} onReorder={reorder} className="space-y-2">
        {krows.map((row, ri) => (
          <RowItem key={row.id} row={row} ri={ri} cols={cols} testid={testid} onCell={setCell} onRemove={removeRow} />
        ))}
      </Reorder.Group>

      <Button data-testid={`${testid}-add-row`} variant="outline" size="sm" onClick={addRow} className="border-white/15 hover:border-gold/50"><Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi riga</Button>
    </div>
  );
}
